import { useState, useEffect } from 'react';
import { statsApi, type StatsResponse, type UserStats } from '@/lib/api';

// Helper to generate CSS styles for color patterns
function getPatternStyle(pattern: string, hex: string): React.CSSProperties {
  const base: React.CSSProperties = { backgroundColor: hex };

  switch (pattern) {
    case 'stripes':
      return {
        ...base,
        backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.4) 3px, rgba(255,255,255,0.4) 6px)`,
      };
    case 'dots':
      return {
        ...base,
        backgroundImage: `radial-gradient(circle at 3px 3px, rgba(255,255,255,0.5) 1.5px, transparent 1.5px)`,
        backgroundSize: '6px 6px',
      };
    case 'diagonal':
      return {
        ...base,
        backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(255,255,255,0.35) 3px, rgba(255,255,255,0.35) 6px)`,
      };
    default:
      return base;
  }
}

function formatWeekOf(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function UsageStatsPage() {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [selectedYear]);

  async function loadStats() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await statsApi.getStats(selectedYear);
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (!stats) return null;

  const maxMonthNights = Math.max(...stats.byMonth.map((m) => m.nights), 1);

  return (
    <div className="space-y-6">
      {/* Header with Year Selector */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Usage Statistics</h1>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {stats.availableYears.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Nights"
          value={stats.overall.totalNights.toString()}
        />
        <StatCard
          label="Total Stays"
          value={stats.overall.totalStays.toString()}
        />
        <StatCard
          label="Occupancy Rate"
          value={`${stats.overall.occupancyRate}%`}
        />
        <StatCard
          label="Avg Stay Length"
          value={`${stats.overall.averageStayLength} nights`}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage by Month */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage by Month</h2>
          <div className="space-y-2">
            {stats.byMonth.map((month) => (
              <div key={month.monthNumber} className="flex items-center gap-3">
                <span className="w-12 text-sm text-gray-600">{month.month.slice(0, 3)}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                  <div
                    className="bg-blue-500 h-full rounded-full transition-all duration-300"
                    style={{ width: `${(month.nights / maxMonthNights) * 100}%` }}
                  />
                </div>
                <span className="w-16 text-sm text-gray-700 text-right">
                  {month.nights} nights
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Share by User */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Share by User</h2>
          {stats.byUser.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No bookings for this year</p>
          ) : (
            <>
              {/* Pie Chart Representation */}
              <div className="flex justify-center mb-6">
                <div className="relative w-48 h-48">
                  <PieChart users={stats.byUser} />
                </div>
              </div>
              {/* Legend */}
              <div className="space-y-2">
                {stats.byUser.map((user) => (
                  <div key={user.userId} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={getPatternStyle(user.userColorPattern, user.userColor)}
                    />
                    <span className="flex-1 text-sm text-gray-700 truncate">{user.userName}</span>
                    <span className="text-sm font-medium text-gray-900">{user.percentageOfTotal}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* User Breakdown Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">User Breakdown</h2>
        </div>
        {stats.byUser.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No bookings for this year</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nights
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stays
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shortest
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Longest
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Average
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Share
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.byUser.map((user) => (
                  <tr key={user.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex-shrink-0"
                          style={getPatternStyle(user.userColorPattern, user.userColor)}
                        />
                        <span className="text-sm font-medium text-gray-900">{user.userName}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                      {user.totalNights}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                      {user.totalStays}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                      {user.shortestStay}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                      {user.longestStay}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700">
                      {user.averageStay}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                      {user.percentageOfTotal}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Busiest Periods */}
      {(stats.overall.busiestMonth || stats.overall.busiestWeek) && (
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex flex-wrap gap-4 text-sm text-blue-800">
            {stats.overall.busiestMonth && (
              <span>
                <strong>Busiest Month:</strong> {stats.overall.busiestMonth.month} ({stats.overall.busiestMonth.nights} nights)
              </span>
            )}
            {stats.overall.busiestWeek && (
              <span>
                <strong>Busiest Week:</strong> Week of {formatWeekOf(stats.overall.busiestWeek.weekOf)} ({stats.overall.busiestWeek.nights} nights)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function PieChart({ users }: { users: UserStats[] }) {
  if (users.length === 0) return null;

  // Calculate pie segments
  let cumulativePercent = 0;
  const segments = users.map((user) => {
    const startPercent = cumulativePercent;
    cumulativePercent += user.percentageOfTotal;
    return {
      user,
      startPercent,
      endPercent: cumulativePercent,
    };
  });

  // Create SVG path for each segment
  function getCoordinatesForPercent(percent: number) {
    const x = Math.cos(2 * Math.PI * (percent / 100));
    const y = Math.sin(2 * Math.PI * (percent / 100));
    return [x, y];
  }

  return (
    <svg viewBox="-1 -1 2 2" style={{ transform: 'rotate(-90deg)' }}>
      {segments.map((segment) => {
        const [startX, startY] = getCoordinatesForPercent(segment.startPercent);
        const [endX, endY] = getCoordinatesForPercent(segment.endPercent);
        const largeArcFlag = segment.user.percentageOfTotal > 50 ? 1 : 0;

        const pathData = [
          `M ${startX} ${startY}`,
          `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
          'L 0 0',
        ].join(' ');

        return (
          <path
            key={segment.user.userId}
            d={pathData}
            fill={segment.user.userColor}
          />
        );
      })}
    </svg>
  );
}
